import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useState } from 'react';

import { useChatDocumentContext } from './ChatDocumentContext.tsx';

import {
  ChatContextType,
  ChatExchange,
  ChatListItem,
  ChatListResponse,
  ChatSession,
  CreateChatExchangeParams,
  CreateChatExchangeRequest,
  createChatExchangeResponse,
} from '~/components/Chat/types.ts';
import { get, post } from '~/utils/request';

const CHAT_REQUEST_TIMEOUT = 3 * 60 * 1000; // 3 minutes
const ChatContext = createContext<ChatContextType | null>(null);

export const useClaimChatsContext = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within a ChatProvider');
  return ctx;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [chatTitle, setChatTitle] = useState<string | null>(null);
  const [greeting, setGreeting] = useState<string | null>(null);
  const [exchanges, setExchanges] = React.useState<ChatExchange[]>([]);
  const [chats, setChats] = useState<ChatListItem[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isChatsLoading, setIsChatsLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatsError, setChatsError] = useState<string | null>(null);

  const { reset } = useChatDocumentContext();

  const resetExchanges = useCallback(() => {
    setExchanges([]);
    setChatTitle(null);
    setGreeting(null);
    setChatError(null);
    reset();
  }, [reset]);

  const fetchChats = useCallback(async (claimNumber: string) => {
    setIsChatsLoading(true);
    setChatsError(null);
    try {
      const response = await get<ChatListResponse>({
        url: `/api/v1/chats/${claimNumber}/all`,
      });
      setChats(response.chats);
    } catch (error) {
      setChatsError('Failed to fetch claim chats. Please try to refresh the page.');
      console.error('Error fetching chats:', error);
    } finally {
      setIsChatsLoading(false);
    }
  }, []);

  const getChatExchanges = useCallback(async (claimNumber: string, chatId?: string) => {
    setIsChatLoading(true);
    setChatError(null);
    try {
      const url = `/api/v1/chats/${claimNumber}`;
      const response = await get<ChatSession>({ url, params: { chat_id: chatId } });

      setChatTitle(response.chat_title);
      setGreeting(response.greeting);
      setExchanges(response.exchanges || []);
    } catch (error) {
      setChatError('Failed to fetch chat exchanges. Please try to refresh the page.');
      console.error('Error fetching chat exchanges:', error);
    } finally {
      setIsChatLoading(false);
    }
  }, []);

  const createTempExchange = (
    userInput: string,
    contextDocumentIds: string[] = [],
    documentUsedIds: string[] = []
  ): ChatExchange => ({
    id: 'temporary-exchange-id',
    created_at: new Date().toISOString(),
    user_input: userInput,
    response: '',
    documents_used: documentUsedIds,
    context_document_source_ids: contextDocumentIds,
  });

  const createChatExchange = useCallback(async (params: CreateChatExchangeParams) => {
    const { claimNumber, userInput, chatId, usedDocuments = [] } = params;
    setIsChatLoading(true);
    setChatError(null);
    try {
      const data: CreateChatExchangeRequest = {
        chat_id: chatId || null,
        claim_number: claimNumber,
        user_input: userInput,
        context_document_source_ids: usedDocuments,
      };

      // on every new user message, we create a temporary exchange to show the user message on the UI.
      // This will be replaced with the actual response once the API call is complete and overrides the Exchanges.
      const tempExchangeForUI = createTempExchange(userInput, usedDocuments);

      setExchanges((prev) => [...prev, tempExchangeForUI]);

      const url = `/api/v1/chats/${claimNumber}`;
      const response = await post<createChatExchangeResponse>({ url, data, timeout: CHAT_REQUEST_TIMEOUT });
      setExchanges(response.exchanges || []);

      return response.chat_id;
    } catch (error) {
      setChatError('Failed to create chat exchange. Please try again.');
      console.error('Error creating chat exchange:', error);
    } finally {
      setIsChatLoading(false);
    }
  }, []);

  return (
    <ChatContext.Provider
      value={{
        greeting,
        chatTitle,
        isChatLoading,
        isChatsLoading,
        chatError,
        chatsError,
        getChatExchanges,
        createChatExchange,
        exchanges,
        chats,
        resetExchanges,
        fetchChats,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
